import axios from "axios";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { NavLink, useHistory } from "react-router-dom";
import { HTTP_STATUSES, STANDARD_ERRORS_WAY } from "../../../../../utils/consts";
import { getServiceNameWithNickname } from "../../../../../utils/getServiceNameWithNickname";
import { parseAxiosError } from "../../../../../utils/response";
import userAuthenticationConfig from "../../../../../utils/userAuthenticationConfig";
import CreateBalancesDialog from "./createBalances/CreateBalancesDialog";
import CreateConnectionDialog from "./createConnection/CreateConnectionDialog";
import GoToBackButton from "../../../../elements/goToBack/GoToBack";
import UserServicesSwitch from "../../../../elements/services/UserServicesSwitch";
import PageSpinner from "../../../../elements/spinner/PageSpinner";
import Notification from "../../../../elements/notification/Notification";
import ConnectionsList from "./ConnectionsList";

import { StyledServiceDetailsWrapper } from "../../../../services/styledServiceDetails";
import DefaultButton from "../../../../styles/materialButtonStyle";
import MaterialDialog from "../../../../styles/materialDialog";
import { StyledContentWrapper } from "../../../../styles/styledMain";
import {
  StyledCol,
  StyledTable,
  StyledTableBody,
  StyledTableHead,
  StyledTableRow
} from "../../../../styles/styledTable";
import { StyledDocumentTitle, StyledDocumentWrapper } from "../../../../styles/styledTextDocument";

const ServiceDetailsContainer = ({ match }) => {

  const { t } = useTranslation("servicePage");
  const [serviceDetails, setServiceDetails] = useState(null);
  const [connections, setConnections] = useState(null);
  const [openConnectionModal, setOpenConnectionModal] = useState(false);
  const [openBalancesModal, setOpenBalancesModal] = useState(false);
  const [userService, setUserService] = useState(null);

  const history = useHistory();

  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  const fetchServiceDetails = () => {
    axios.get("/api/services/" + match.params.serviceId, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        setServiceDetails(response.data);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const fetchConnections = () => {
    const filterData = "?service.id=" + match.params.serviceId + "&project.user.id=" + match.params.userId;

    axios.get("/api/connections" + filterData, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        setConnections(response.data["hydra:member"]);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const fetchUserService = () => {
    const filterData = "?service.id=" + match.params.serviceId + "&user.id=" + match.params.userId;

    axios.get("/api/user-services" + filterData, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        if (response.data["hydra:totalItems"]) {
          setUserService(response.data["hydra:member"][0].id);

          return;
        }

        setUserService(null);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const handleOpenConnection = () => {
    if (!userService) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("details.connections.enableServiceFirst")
      });

      return;
    }

    setOpenConnectionModal(true);
  };

  const handleOpenBalances = () => {
    if (!userService) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("details.connections.enableServiceFirst")
      });

      return;
    }

    setOpenBalancesModal(true);
  };

  useEffect(() => {
    fetchServiceDetails();
    fetchConnections();
    fetchUserService();
  }, []);

  if (!serviceDetails) {
    return <PageSpinner
      position="center"
      pt="60"
      pb="60"
    />;
  }

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <StyledContentWrapper>
        <Helmet>
          <title>{serviceDetails.title} • eXsoft.io</title>
        </Helmet>
        <StyledServiceDetailsWrapper>
          <div className="services-details-head">
            <GoToBackButton handler={() => {history.goBack();}} />
          </div>
          <StyledDocumentWrapper>
            <StyledDocumentTitle
              as="h2"
              size="18"
            >
              {serviceDetails.title}
            </StyledDocumentTitle>
            <StyledTable
              size="14"
              className="access-level-table"
            >
              <StyledTableHead
                col="2"
                className="access-level-table__head"
              >
                <StyledCol>{t("details.key")}:</StyledCol>
                <StyledCol>{t("details.value")}:</StyledCol>
              </StyledTableHead>
              <StyledTableBody className="access-level-table__body">
                <StyledTableRow
                  col="2"
                  className="access-level-table__row"
                >
                  <StyledCol data-title={t("details.key")}>
                    Status
                  </StyledCol>
                  <StyledCol data-title={t("details.value")}>
                    <UserServicesSwitch
                      userId={match.params.userId}
                      userService={{ id: match.params.serviceId, userServiceId: userService }}
                      notification={notification}
                      setNotification={setNotification}
                      refetch={fetchUserService}
                    />
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow
                  col="2"
                  className="access-level-table__row"
                >
                  <StyledCol data-title={t("details.key")}>
                    ID
                  </StyledCol>
                  <StyledCol data-title={t("details.value")}>
                    {serviceDetails.id}
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow
                  col="2"
                  className="access-level-table__row"
                >
                  <StyledCol data-title={t("details.key")}>
                    Name
                  </StyledCol>
                  <StyledCol data-title={t("details.value")}>
                    {getServiceNameWithNickname(serviceDetails.name, serviceDetails.nickname)}
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow
                  col="2"
                  className="access-level-table__row"
                >
                  <StyledCol data-title={t("details.key")}>
                    Title
                  </StyledCol>
                  <StyledCol data-title={t("details.value")}>
                    {serviceDetails.title}
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow
                  col="2"
                  className="access-level-table__row"
                >
                  <StyledCol data-title={t("details.key")}>
                    Tag
                  </StyledCol>
                  <StyledCol data-title={t("details.value")}>
                    {serviceDetails.tag}
                  </StyledCol>
                </StyledTableRow>
              </StyledTableBody>
            </StyledTable>
          </StyledDocumentWrapper>

          <StyledDocumentTitle
            as="h2"
            size="18"
          >
            {t("details.connections.userConnections")}
          </StyledDocumentTitle>
          <div className="services-details__actions">
            <DefaultButton
              color="success"
              onClick={handleOpenConnection}
            >
              <span className="icon-light-add-connection"/>
            </DefaultButton>
            <MaterialDialog
              open={openConnectionModal}
            >
              <CreateConnectionDialog
                t={t}
                setOpen={setOpenConnectionModal}
                notification={notification}
                setNotification={setNotification}
                serviceId={match.params.serviceId}
                userId={match.params.userId}
                setConnections={setConnections}
              />
            </MaterialDialog>

            <DefaultButton
              color="transparent"
              onClick={handleOpenBalances}
            >
              {t("details.balancesForm.button")}
            </DefaultButton>
            <MaterialDialog
              open={openBalancesModal}
            >
              <CreateBalancesDialog
                t={t}
                setOpen={setOpenBalancesModal}
                notification={notification}
                setNotification={setNotification}
                serviceId={match.params.serviceId}
                userId={match.params.userId}
              />
            </MaterialDialog>
          </div>
          <ConnectionsList t={t} connections={connections} fetchConnections={fetchConnections}/>
        </StyledServiceDetailsWrapper>
      </StyledContentWrapper>
    </>
  );
};

export default ServiceDetailsContainer;